import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/solid";

import { useConfirmation } from "context/Confirmation";

import { StatusBadge } from "./StatusBadge";
import { SeverityBadge } from "./SeverityBadge";
import { AIHazardShell, AIHazardStatus } from "./types";

interface HazardCardProps {
  index: number; // 1-based position within the group
  hazard: AIHazardShell;
  projectId: string;
  // Whether hazard.accepted_risk_resource_id still exists in project.risks —
  // when it doesn't (risk deleted meanwhile), the deep-link is replaced by a
  // plain "accepted" badge variant (plan §5).
  acceptedRiskExists: boolean;
  onDismiss: (hazard: AIHazardShell) => void;
  onRestore: (hazard: AIHazardShell) => void;
}

const CLAMP_2_LINES: React.CSSProperties = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const STATUS_LEFT_BORDER: Record<AIHazardStatus, string> = {
  [AIHazardStatus.suggested]: "border-l-4 border-l-blue-400",
  [AIHazardStatus.generated]: "border-l-4 border-l-cyan-500",
  [AIHazardStatus.accepted]: "border-l-4 border-l-green-500",
  [AIHazardStatus.dismissed]: "border-l-4 border-l-gray-300",
};

export const HazardCard = ({
  index,
  hazard,
  projectId,
  acceptedRiskExists,
  onDismiss,
  onRestore,
}: HazardCardProps) => {
  const { t } = useTranslation();
  const { openConfirmation } = useConfirmation();
  const [reasoningOpen, setReasoningOpen] = useState(false);

  const detailLocation = `/projects/${projectId}/risks/ai/hazards/${hazard.id}`;
  const isDismissed = hazard.status === AIHazardStatus.dismissed;
  const isAccepted = hazard.status === AIHazardStatus.accepted;

  // No silent removal (plan §1.2 item 3): dismissal always goes through the
  // app-wide confirmation dialog; dismissed cards stay visible + restorable.
  const handleDismiss = () => {
    openConfirmation(() => onDismiss(hazard), {
      label: t("risks.ai.card.dismiss.confirm.label"),
      subline: t("risks.ai.card.dismiss.confirm.subline"),
    });
  };

  return (
    <div
      data-test={`ai-hazard-card-${hazard.id}`}
      className={`border rounded-lg p-4 bg-white overflow-hidden ${
        isDismissed ? "border-gray-200 opacity-60" : "border-gray-200"
      } ${STATUS_LEFT_BORDER[hazard.status]}`}
    >
      <div className="sm:flex sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">
            <span className="text-gray-400 mr-2">{index}.</span>
            {hazard.name}
          </p>
          <p className="mt-1 text-sm text-gray-500" style={CLAMP_2_LINES}>
            {hazard.description}
          </p>
        </div>
        <div className="mt-2 sm:mt-0 flex flex-wrap items-center gap-2 flex-shrink-0">
          <SeverityBadge severity={hazard.severity} />
          <StatusBadge
            status={hazard.status}
            acceptedRiskMissing={isAccepted && !acceptedRiskExists}
          />
          <span className="text-xs text-gray-400">
            {t(`form.risk.options.group.${hazard.group_id_hint}`)}
          </span>
        </div>
      </div>

      <div className="mt-2">
        <button
          type="button"
          onClick={() => setReasoningOpen((prev) => !prev)}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 focus:outline-none"
        >
          {reasoningOpen ? (
            <ChevronUpIcon className="h-3 w-3" aria-hidden="true" />
          ) : (
            <ChevronDownIcon className="h-3 w-3" aria-hidden="true" />
          )}
          {t("risks.ai.shell.reasoning.headline")}
        </button>
        {reasoningOpen && (
          <p className="mt-1 text-sm text-gray-600 whitespace-pre-line">
            {hazard.reasoning}
          </p>
        )}
      </div>

      <div className="mt-3 flex items-center gap-4">
        {hazard.status === AIHazardStatus.suggested && (
          <Link
            to={detailLocation}
            className="text-sm font-medium text-cyan-600 hover:text-cyan-700"
          >
            {t("risks.ai.card.open")}
          </Link>
        )}

        {hazard.status === AIHazardStatus.generated && (
          <Link
            to={detailLocation}
            className="text-sm font-medium text-cyan-600 hover:text-cyan-700"
          >
            {t("risks.ai.card.continue")}
          </Link>
        )}

        {isAccepted && (
          <>
            <Link
              to={detailLocation}
              className="text-sm font-medium text-cyan-600 hover:text-cyan-700"
            >
              {t("risks.ai.card.open")}
            </Link>
            {acceptedRiskExists && (
              <Link
                to={`/projects/${projectId}/risks/${hazard.accepted_risk_resource_id}/edit`}
                className="text-sm font-medium text-cyan-600 hover:text-cyan-700"
              >
                {t("risks.ai.card.viewRisk")}
              </Link>
            )}
          </>
        )}

        {!isAccepted && !isDismissed && (
          <button
            type="button"
            onClick={handleDismiss}
            className="text-sm font-medium text-gray-500 hover:text-red-600"
          >
            {t("risks.ai.card.dismiss.cta")}
          </button>
        )}

        {isDismissed && (
          <button
            type="button"
            onClick={() => onRestore(hazard)}
            className="text-sm font-medium text-cyan-600 hover:text-cyan-700"
          >
            {t("risks.ai.card.restore")}
          </button>
        )}
      </div>
    </div>
  );
};
